import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';
import api from './api';
import {
  CompanyInstance,
  CompanyInstanceCreate,
  CompanyInstanceUpdate,
  CompanyInstanceFilters,
} from '../../types/companyInstance';

export class CompanyInstanceService extends BaseService {
  /**
   * Get all company instances with optional filtering
   */
  static async getAll(filters?: CompanyInstanceFilters): Promise<CompanyInstance[]> {
    try {
      let endpoint = API_ENDPOINTS.COMPANY_INSTANCES.BASE;
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.user_id) params.append('user_id', filters.user_id);
        if (filters.company_id) params.append('company_id', filters.company_id);
        if (filters.active_only !== undefined) params.append('active_only', String(filters.active_only));
        if (filters.limit) params.append('limit', String(filters.limit));
        if (filters.offset) params.append('offset', String(filters.offset));
      }
      
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
      
      return this.get<CompanyInstance[]>(endpoint);
    } catch (error) {
      console.error('[CompanyInstanceService] Error fetching company instances:', error);
      return [];
    }
  }

  /**
   * Get company instance by ID
   */
  static async getById(id: string): Promise<CompanyInstance | null> {
    try {
      return await this.get<CompanyInstance>(API_ENDPOINTS.COMPANY_INSTANCES.BY_ID(id));
    } catch (error) {
      console.error('[CompanyInstanceService] Error fetching company instance:', error);
      return null;
    }
  }

  /**
   * Create a new company instance
   */
  static async create(data: CompanyInstanceCreate): Promise<CompanyInstance> {
    return this.post<CompanyInstance>(
      API_ENDPOINTS.COMPANY_INSTANCES.BASE,
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Update company instance
   */
  static async update(id: string, data: CompanyInstanceUpdate): Promise<CompanyInstance> {
    return this.put<CompanyInstance>(
      API_ENDPOINTS.COMPANY_INSTANCES.BY_ID(id),
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Delete company instance
   */
  static async delete(id: string): Promise<void> {
    try {
      await api.delete(API_ENDPOINTS.COMPANY_INSTANCES.BY_ID(id));
    } catch (error: any) {
      // Suppress CORS/network errors
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message?.includes('CORS') ||
                         error.message?.includes('Access-Control-Allow-Origin');
      if (!isCorsError) {
        console.error(`DELETE request failed for ${API_ENDPOINTS.COMPANY_INSTANCES.BY_ID(id)}:`, error);
      }
      throw error;
    }
  }

  /**
   * Get artifacts for a company instance
   */
  static async getArtifacts(instanceId: string, filters?: { artifact_type?: string; status?: string; limit?: number; offset?: number }): Promise<any[]> {
    try {
      let endpoint = API_ENDPOINTS.COMPANY_INSTANCES.ARTIFACTS(instanceId);
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.artifact_type) params.append('artifact_type', filters.artifact_type);
        if (filters.status) params.append('status', filters.status);
        if (filters.limit) params.append('limit', String(filters.limit));
        if (filters.offset) params.append('offset', String(filters.offset));
      }
      
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
      
      return this.get<any[]>(endpoint);
    } catch (error) {
      console.error('[CompanyInstanceService] Error fetching instance artifacts:', error);
      return [];
    }
  }

  /**
   * Get users in a company instance
   */
  static async getUsers(instanceId: string): Promise<any[]> {
    try {
      return this.get<any[]>(API_ENDPOINTS.COMPANY_INSTANCES.USERS(instanceId));
    } catch (error) {
      console.error('[CompanyInstanceService] Error fetching instance users:', error);
      return [];
    }
  }

  /**
   * Invite user to company instance
   */
  static async inviteUser(instanceId: string, email: string, instanceName?: string): Promise<any> {
    return this.post<any>(
      API_ENDPOINTS.COMPANY_INSTANCES.INVITE(instanceId),
      {
        email,
        instance_name: instanceName,
      } as unknown as Record<string, unknown>
    );
  }

  /**
   * Search company instances
   */
  static async search(searchTerm: string, limit?: number): Promise<CompanyInstance[]> {
    try {
      let endpoint = API_ENDPOINTS.COMPANY_INSTANCES.SEARCH(searchTerm);
      if (limit) {
        endpoint += `&limit=${limit}`;
      }
      return this.get<CompanyInstance[]>(endpoint);
    } catch (error) {
      console.error('[CompanyInstanceService] Error searching company instances:', error);
      return [];
    }
  }
}
